import React, { useEffect } from 'react';
import { isEqual } from 'lodash';
import { withStyles } from '@material-ui/core/styles';

import AppBar from '@material-ui/core/AppBar';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Skeleton from '@material-ui/lab/Skeleton';

// import fragments Tabs
import { Tabs, Tab, TabPanel, a11yProps } from './../../fragments/Tabs';

// import globalStyles
import globalStyles from './../../../styles/globalStyles';

// import useSnackbar
import { useSnackbar } from 'notistack';

// import constants
import { server } from './../../../constants';

// import utils
import {
  logger,
  userProfiler,
  issueOperations,
  isManageIssue,
  formatDatetime,
  base64,
} from './../../../utils/utils';

//import fragments AppSidebarRBack, IssueCard, CommentCard
import IssueCard from './../../fragments/IssueCard';
import CommentCard from './../../fragments/CommentCard';
import { FormComment } from './../../fragments/FormComment';
import LogCard from './../../fragments/LogCard';
import GridAvatar from './../../fragments/GridAvatar';
// import React-Redux
import { useSelector, useDispatch } from 'react-redux';
import * as issueActions from '../../../actions/issue.action';
import * as issueCommentActions from '../../../actions/issueComment.action';
import * as issueLogActions from '../../../actions/issueLog.action';

// import hooks
import { useHandleClientError } from '../../../utils/HttpClient';

// import file
import { file } from './../../../data';

/**
 * CommentCardList (Component)
 * @param {object} prop
 * @param {array} prop.data
 * @param {func} props.onSubmitByComment  function(dataUpdated) => void
 * @param {func} props.onDroppedFilesByComment function(filesBase64: array, data: object) => void
 * @param {func} props.onRemovedFileByComment
 * @param {bool} readOnly, for check isManageIssue
   @param {bool} isWorker, for check isManageIssue
   @param {int} processStatus, for check isManageIssue
 */
const CommentCardList = React.memo(
  ({
    data = [],
    onSubmitByComment,
    onDroppedFilesByComment,
    onRemovedFileByComment,
    userProfile,
    readOnly,
    isWorker,
    processStatus,
  }) => {
    if (!data.length) {
      return (
        <Typography component="div" variant="subtitle1" color="textSecondary">
          <Box textAlign="left" my={5} mx={10}>
            ไม่มีความคิดเห็น
          </Box>
        </Typography>
      );
    } else {
      return data.map((comment, index) => (
        <CommentCard
          id={comment.IssueComments.id}
          issueId={comment.IssueComments.issue_id}
          issueCommentTypeId={comment.IssueComments.issueCommentType_id}
          issueCommentTypeName={comment.IssueCommentTypes.name}
          issueCommentTypeDescription={comment.IssueCommentTypes.description}
          receiverType={comment.IssueComments.receiverType}
          detail={comment.IssueComments.detail}
          commentFiles={comment.IssueComments.commentFiles}
          creater={`${comment.IssueCommentsCreateBy.firstName} ${comment.IssueCommentsCreateBy.lastName}`}
          avatar={{
            image: comment.IssueCommentsCreateBy.image,
          }}
          createDate={formatDatetime({
            datetime: comment.IssueComments.createDate,
          })}
          modifyDate={
            comment.IssueComments.modifyDate &&
            formatDatetime({
              datetime: comment.IssueComments.modifyDate,
            })
          }
          modifier={
            comment.IssueCommentsModifyBy &&
            `${comment.IssueCommentsModifyBy.firstName} ${comment.IssueCommentsModifyBy.lastName}`
          }
          editing={isManageIssue(readOnly, isWorker, processStatus, {
            operation: issueOperations.updateComment,
            isCreater: comment.IssueComments.createBy === userProfile.user.id,
          })}
          receiverTapping={true}
          onDroppedFiles={onDroppedFilesByComment}
          onRemovedFile={onRemovedFileByComment}
          onSubmit={onSubmitByComment}
          key={index}
        />
      ));
    }
  },
  isEqual
);

// Styles of ManageIssueActivitiesPage
const styles = (theme) => ({
  ...globalStyles(theme),
  root: {
    flexGrow: 1, // for appbar
    margin: theme.spacing(1),
  },
  activityBar: {
    boxShadow: 'none',
    margin: theme.spacing(1, 0),
  },
  formControl: {
    margin: theme.spacing(1, 0, 1, 0),
  },
  card: {
    minWidth: 275,
    padding: theme.spacing(1, 1, 2, 2),
  },
  cardContent: {
    minHeight: 275,
  },
  section: {
    margin: theme.spacing(1),
  },
  formCommentWidth: {
    maxWidth: 890,
  },
  // https://stackoverflow.com/questions/61056265/how-to-override-box-created-by-tabpanel-in-material-ui
  tabPanel: {
    '&>:first-child': {
      padding: theme.spacing(3, 0.5),
    },
  },
  appbarTitle: {
    flexGrow: 1,
  },
});

// NOTE: Dispatch mutiple actions
// https://stackoverflow.com/questions/44321676/where-to-dispatch-multiple-actions-in-redux

/**
 * ManageIssueActivitiesPage (Component)
 * @param {object} prop { classes, match }
 */
const ManageIssueActivitiesPage = React.memo((props) => {
  // Classes extend from widthStyle(style)
  const { classes, match } = props;

  // logger (Dev only)
  logger('ManageIssueActivitiesPage render');

  const issue_id = base64.decode(match.params.id);

  // Get user profile
  const upf = userProfiler.getFromLocal();

  // Hook handle-client-error (handle error in catch from promise reject)
  const handleClientError = useHandleClientError();

  // package (getListActivityPackage) parameter for request getListComment / getListLog
  const getListActivityPackage = {
    package: base64.encodeObj({
      lang: 'TH',
      mode: server.REQ_PARAM_MODE_STAFF,
      issue_id,
    }),
  };

  // package parameter for request getDetail-issue
  const getIssueDetailPackage = {
    package: base64.encodeObj({
      lang: 'TH',
      id: issue_id,
    }),
  };

  // Hook reducer (by useSelector) from store
  const dispatch = useDispatch();

  // Hook enqueueSnackbar from useSnackbar
  const { enqueueSnackbar } = useSnackbar();

  // Hook reducer V2) Destructure multiple reducer by one useSelector
  let { issueReducer, issueCommentReducer, issueLogReducer } = useSelector(
    ({ issueReducer, issueCommentReducer, issueLogReducer }) => ({
      issueReducer,
      issueCommentReducer,
      issueLogReducer,
    })
  );

  // Check empty issueReducer.result assign to initail issueReducer
  let issue = issueReducer.result || {};
  let comments = issueCommentReducer.result || [];
  let logs = issueLogReducer.result || [];

  // dispatch getIssueDetail, getIssueComment, getIssueLog
  useEffect(() => {
    if (match.params.id) {
      dispatch(
        issueActions.getIssueDetail(getIssueDetailPackage, {
          includedViewStatus: true,
        })
      );
      dispatch(issueCommentActions.getIssueComment(getListActivityPackage));
      dispatch(issueLogActions.getIssueLog(getListActivityPackage));
    }
  }, []);

  // function handle update issue
  const handleUpdateIssue = async (data) => {
    try {
      // Dispatch mutiple action (updateIssue, updateIssueLog) solution 2
      // Ref: https://github.com/reduxjs/redux-thunk
      // Ref: https://twitter.com/dan_abramov/status/770914221638942720
      await dispatch(
        issueActions.updateIssue({
          data,
          params: getIssueDetailPackage,
          options: {
            includedViewStatus: true,
          },
        })
      ).then(
        () =>
          dispatch(
            issueLogActions.updateIssueLog(getListActivityPackage)
          ).catch((error) => {
            throw error;
          }),
        (error) => {
          throw error;
        }
      );
      enqueueSnackbar(`แก้ไขข้อมูลแล้ว`, {
        variant: 'success',
      });
    } catch (error) {
      handleClientError(error);
    }
  };

  // Handle add comment-issue (form submit)
  const handleAddComment = async (data) => {
    try {
      // Dispatch mutiple action (addIssueComment, updateIssueLog) with solution 2 (Look solution 1 in handleUpdateIssue)
      await dispatch(
        issueCommentActions.addIssueComment({
          issue_id,
          data,
          params: getListActivityPackage,
        })
      ).then(
        () =>
          dispatch(
            issueLogActions.updateIssueLog(getListActivityPackage)
          ).catch((error) => {
            throw error;
          }),
        (error) => {
          throw error;
        }
      );

      enqueueSnackbar(`เพิ่มความคิดเห็นแล้ว`, {
        variant: 'success',
      });
    } catch (error) {
      handleClientError(error);
    }
  };

  // Handle update comment (form submit)
  const handleUpdateComment = async (data) => {
    try {
      // Dispatch mutiple action (updateIssueComment, updateIssueLog) with solution 2 (Look solution 1 in handleUpdateIssue)
      await dispatch(
        issueCommentActions.updateIssueComment({
          issue_id,
          data,
          params: getListActivityPackage,
        })
      ).then(
        () =>
          dispatch(
            issueLogActions.updateIssueLog(getListActivityPackage)
          ).catch((error) => {
            throw error;
          }),
        (error) => {
          throw error;
        }
      );

      enqueueSnackbar(`แก้ไขความคิดเห็นแล้ว`, {
        variant: 'success',
      });
    } catch (error) {
      handleClientError(error);
    }
  };

  // Handle update files of comment (form submit)
  const handleDropFilesByComment = async (newFiles, extendNewFiles) => {
    // Save file (file.save, request api image/vx/save) and get filesSaved
    let filesSaved = await file.save({
      data: newFiles.map((file) => ({
        issue_id,
        name: file.name,
        type: file.type,
        content: file.base64,
      })),
    });

    // Callback, Add file id (in filesSaved, field: id) to files (each nfile)
    await extendNewFiles((nfile) => {
      let fileSaved = filesSaved.find((fs) => fs.name === nfile.name);
      if (fileSaved !== undefined) {
        return { id: fileSaved.id };
      }
      return { id: 'n/a' };
    });
  };

  // TabPanel state & handle function
  const [tabValue, setTabValue] = React.useState(0);

  // TabPanel handle function
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <div className={classes.root}>
      {/* issue loading (by Skeleton) */}
      {issueReducer.isLoading && (
        <GridAvatar name={<Skeleton variant="circle" width={30} height={30} />}>
          <Skeleton width="100%" variant="text" style={{ marginBottom: 6 }} />
          <Skeleton width="100%" variant="text" />
          <Skeleton width="100%" variant="text" />
          <Skeleton width="80%" variant="text" style={{ marginBottom: 6 }} />
          <Skeleton variant="rect" width="100%" height={60} />
        </GridAvatar>
      )}
      {/* Issue card data from API */}
      {!issueReducer.isLoading && issue.issues && (
        <IssueCard
          id={issue.issues.id}
          summary={issue.issues.summary}
          description={issue.issues.description}
          contract={issue.issues.contract}
          vote={issue.issues.vote}
          createDate={formatDatetime({
            datetime: issue.issues.createDate,
          })}
          reporter={issue.issues.fullname}
          avatar={{
            image: issue.issuesCreateBy.image,
          }}
          modifyDate={
            issue.issues.modifyDate &&
            formatDatetime({
              datetime: issue.issues.modifyDate,
            })
          }
          modifier={
            issue.issueModifyBy &&
            `${issue.issueModifyBy.firstName} ${issue.issueModifyBy.lastName}`
          }
          thumbnails={issue.issues.thumbnails}
          onDroppedFiles={async (newFiles, extendNewFiles) => {
            // Save file (file.save, request api image/vx/save) and get filesSaved
            let filesSaved = await file.save({
              data: newFiles.map((file) => ({
                issue_id,
                name: file.name,
                type: file.type,
                content: file.base64,
              })),
            });

            // Callback, Add file id (in filesSaved, field: id) to files (each nfile)
            await extendNewFiles((nfile) => {
              let fileSaved = filesSaved.find((fs) => fs.name === nfile.name);
              if (fileSaved !== undefined) {
                return { id: fileSaved.id };
              }
              return { id: 'n/a' };
            });
          }}
          onSubmit={handleUpdateIssue}
          editing={isManageIssue(
            issue.readOnlyStatus.readOnly,
            issue.issues.worker === upf.user.id,
            issue.issues.processStatus_id,
            {
              operation: issueOperations.updateIssue,
              isCreater: upf.user.id === issue.issues.createBy,
            }
          )}
          isClosed={issue.issueStatuses.id === 90}
          closing={false}
        />
      )}

      <Divider />
      <Typography component="div">
        <Box letterSpacing={1} m={2} fontSize="h6.fontSize">
          กิจกรรม
        </Box>
      </Typography>
      <AppBar position="static" color="default">
        <Tabs
          value={tabValue}
          onChange={handleChangeTab}
          aria-label="activity tabs"
        >
          <Tab label="ความคิดเห็น" {...a11yProps(0)} />
          <Tab label="ประวัติ" {...a11yProps(1)} />
          {/* <Tab label="วิเคราะห์เหตุละเมิด" {...a11yProps(2)} /> */}
        </Tabs>
      </AppBar>
      <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
        <div>
          {/* issue loading (by Skeleton) */}
          {issueCommentReducer.isLoading && (
            <GridAvatar
              name={<Skeleton variant="circle" width={30} height={30} />}
            >
              <Skeleton variant="text" />
              <Skeleton width="100%" variant="text" />
              <Skeleton width="100%" variant="text" />
              <Skeleton
                width="80%"
                variant="text"
                style={{ marginBottom: 6 }}
              />
            </GridAvatar>
          )}
          {!issueCommentReducer.isLoading && issue.issues && (
            <CommentCardList
              data={comments}
              userProfile={upf}
              readOnly={issue.readOnlyStatus.readOnly}
              isWorker={issue.issues.worker === upf.user.id}
              processStatus={issue.issues.processStatus_id}
              onDroppedFilesByComment={handleDropFilesByComment}
              onSubmitByComment={handleUpdateComment}
            />
          )}
          {!issueCommentReducer.isLoading && issue.issues && (
            <React.Fragment>
              {isManageIssue(
                issue.readOnlyStatus.readOnly,
                issue.issues.worker === upf.user.id,
                issue.issues.processStatus_id,
                {
                  operation: issueOperations.addComment,
                }
              ) && (
                <GridAvatar image={upf.user.image}>
                  <FormComment
                    receiverTapping
                    onDroppedFiles={async (newFiles, extendNewFiles) => {
                      // Save file (file.save, request api image/vx/save) and get filesSaved
                      let filesSaved = await file.save({
                        data: newFiles.map((file) => ({
                          issue_id,
                          name: file.name,
                          type: file.type,
                          content: file.base64,
                        })),
                      });

                      // Callback, Add file id (in filesSaved, field: id) to files (each nfile)
                      await extendNewFiles((nfile) => {
                        let fileSaved = filesSaved.find(
                          (fs) => fs.name === nfile.name
                        );
                        if (fileSaved !== undefined) {
                          return { id: fileSaved.id };
                        }
                        return { id: 'n/a' };
                      });
                    }}
                    onSubmit={handleAddComment}
                    formControlStyle={classes.formCommentWidth}
                  />
                </GridAvatar>
              )}
            </React.Fragment>
          )}
        </div>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <div align="left">
          {!issueLogReducer.isLoading && <LogCard logs={logs} />}
        </div>
      </TabPanel>
      {/* <TabPanel value={tabValue} index={2}>
        <div align="left">
          <hุุ6>Under Construction</hุุ6>
        </div>
      </TabPanel> */}
    </div>
  );
});

export default withStyles(styles)(ManageIssueActivitiesPage);
